import "./AssigneeComponent.less";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { FormValidationHelpers } from "../../../helpers/FormValidationHelpers";
import { Logger } from "../../../services/Logger";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import LinkComponent from "../linkComponent/LinkComponent";
import { MultiSelectTagPicker } from "../../../components/_base/MultiSelectTagPicker";

export interface IPersonaProps {
    text?: string;
    secondaryText?: string;
    data?: {
        nrn?: string;
        email?: string;
        [key: string]: any;
    };
    [key: string]: any;
}

interface ITag {
    key: string;
    name?: string;
}

type MessageBarIntent = number;

const SEVERE_WARNING_MESSAGE_BAR_TYPE = 3;

export interface AssigneeComponentProps extends LocalizeContextProps {
    testid: string;
    logger: Logger;
    projectsService: IProjectsService;
    assignees: IPersonaProps[];
    formValidationHelpers: FormValidationHelpers;
    selectedProject: ITag | null;
    currentUserEmail: string | null;
    disabled: boolean;
    label: string;
    required: boolean;
    onAssigneeChanged: (assignees: IPersonaProps[]) => void;
    onError?: (message: string | null, type: MessageBarIntent) => void;
    showAssignToMe?: boolean;
    maxAllowed?: number;
    type?: string;
    sameCcAndTo?: boolean;
    panelString?: string;
    sameToAndCcEmail?: string[];
    emailGroup?: IPersonaProps[];
    placeholder?: string;
    // Optional label shown beside the spinner while team members are being searched. Only consumers
    // that opt in (currently the RFI page) render it; other pages keep the bare spinner.
    loadingText?: string;
}

const useStyles = makeStyles({
    optionEmailSubtext: {
        color: tokens.colorNeutralForeground3,
    },
    optionRow: {
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
    },
    optionTextColumn: {
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
    },
    optionText: {
        display: "block",
        minWidth: 0,
        flex: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    optionSecondaryText: {
        display: "block",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
});

const AssigneeFluent9Component = (props: AssigneeComponentProps) => {
    const styles = useStyles();
    const [resolvedOptions, setResolvedOptions] = useState<IPersonaProps[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
    const requestIdRef = useRef<number>(0);
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef<boolean>(true);

    // Runs this on unmount. Blocks state updates while unmounted
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            requestIdRef.current += 1;
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
        };
    }, []);

    const onAssignedToFiltered = async (filter: string): Promise<IPersonaProps[]> => {
        props.logger.info(`[${props.type}] onAssignedToFiltered`);
        const projectNrn = props.selectedProject?.key;
        if (!filter || !projectNrn) {
            return [];
        }
        let personaProps: IPersonaProps[];
        const currentAssigneeNrns: (string | undefined)[] = props.assignees.map(
            (assignee) => (assignee as any).data?.nrn
        );
        try {
            const teamMembers = await props.projectsService.getAllTeamMembersNormalized(projectNrn, filter);

            personaProps = props.formValidationHelpers.getPersonaPropsFromTeamMembers(teamMembers);
            personaProps = personaProps.filter((personaProp) => {
                if (!(personaProp as any).data?.nrn) {
                    return false;
                }
                return !currentAssigneeNrns.includes((personaProp as any).data?.nrn);
            });
        } catch {
            personaProps = [];
        }

        return personaProps;
    };

    const onAssignedToChanged = (items?: IPersonaProps[]) => {
        if (!items || !items.length) {
            updateAssignees([]);
            return;
        }

        // remove the last entry from the items list..
        let recentlyAdded = items.pop() as IPersonaProps & { data?: { email?: string } };
        // Checks for any duplicates, for each item, it compares the email, AND the text
        // and if any matches... filters the list to remove them
        let uniqueItems = items.filter((item) => {
            if (item.text === recentlyAdded.data?.email) {
                return false;
            }
            if (item.text === recentlyAdded.text) {
                return false;
            }
            if ((item as any).data?.email === recentlyAdded.text) {
                recentlyAdded = item; // if there was an alternate 'expanded' form of the recent item, remove it.. but update the recent item
                return false;
            }
            return true;
        });
        uniqueItems.push(recentlyAdded);
        if (props.maxAllowed) {
            uniqueItems = uniqueItems.slice(-props.maxAllowed);
        }
        updateAssignees(uniqueItems);
    };

    const getValidationMessage = (currentAssignees: IPersonaProps[] | null): string => {
        let isSame: boolean | undefined = false;
        let isFormatError = false;

        if (currentAssignees) {
            currentAssignees.forEach((assignee, index) => {
                const email = assignee.data?.email ?? assignee.text ?? "";
                props.logger.info(`[${props.type}]  ${index} : ${email}`);
                if (email) {
                    // Team groups have a group name instead of an email, so they would fail the email
                    // format check. Mirror the legacy AssigneeComponent and treat any assignee that
                    // matches a preloaded team group (by display name or email) as a valid group.
                    const isValidGroup = props.emailGroup?.some(
                        (group) => group.text === email || group.data?.email === email
                    );
                    if (!props.formValidationHelpers.isValidEmail(email, isValidGroup)) {
                        isFormatError = true;
                    }
                    isSame = isSame || props.sameToAndCcEmail?.some((p) => p === email);
                }
            });
        }

        const shouldShowEmailFormatErrorMessage = props.type === "cc" ? isFormatError && !isSame : isFormatError;
        const shouldShowCcSameError =
            !!props.sameCcAndTo &&
            (currentAssignees?.length ?? props.assignees.length) > 0 &&
            !shouldShowEmailFormatErrorMessage;

        if (shouldShowEmailFormatErrorMessage) {
            return props.translate("SHARED.ERRORS.EMAIL_VALIDATION") as string;
        }

        if (shouldShowCcSameError) {
            return `${props.translate("SHARED.ERRORS.TO_AND_CC_VALIDATION_1")}${props.translate(
                "SHARED.ERRORS.TO_AND_CC_VALIDATION_2"
            )}${props.translate("SHARED.ERRORS.TO_AND_CC_VALIDATION_3")}${props.translate(
                "SHARED.ERRORS.TO_AND_CC_VALIDATION_4"
            )}${props.translate("SHARED.ERRORS.TO_AND_CC_VALIDATION_5")}`;
        }

        return "";
    };

    const validationMessage = useMemo(() => getValidationMessage(props.assignees), [
        props.assignees,
        props.sameCcAndTo,
        props.sameToAndCcEmail,
        props.emailGroup,
        props.translate,
        props.type,
    ]);

    /**
     * Helper: Convert IPersonaProps object to string representation used for picker value/filtering.
     * Include both name and email (when both are present) so users can search by either.
     */
    const personaPropsToString = (persona: IPersonaProps): string => {
        const name = persona.text?.trim() || "";
        const email = persona.data?.email?.trim() || "";

        if (name && email && name.toLowerCase() !== email.toLowerCase()) {
            return `${name} ${email}`;
        }

        return name || email || "";
    };

    /**
     * Helper: Convert string back to IPersonaProps object by finding in resolved options, existing assignees, or creating new
     */
    const stringToPersonaProps = (str: string, availablePersonas: IPersonaProps[]): IPersonaProps => {
        // First check resolved options
        let found = availablePersonas.find((p) => personaPropsToString(p) === str);
        if (found) {
            return found;
        }
        // Then check existing assignees to preserve full object metadata
        found = props.assignees.find((p) => personaPropsToString(p) === str);
        if (found) {
            return found;
        }
        // Only then create a new object
        return { text: str };
    };

    /**
     * Handle async filtering when user types in the picker input
     */
    const handleInput = (query: string): void => {
        props.logger.info(`[${props.type}] onFilterInputChanged: "${query}"`);

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }

        const requestId = ++requestIdRef.current;

        if (!query) {
            setResolvedOptions([]);
            setIsLoadingOptions(false);
            return;
        }

        setIsLoadingOptions(true);

        debounceTimeoutRef.current = setTimeout(async () => {
            try {
                const filtered = await onAssignedToFiltered(query);

                // Block stale state updates, or block state updates if the component isn't mounted anymore
                if (requestId !== requestIdRef.current || !isMountedRef.current) {
                    return;
                }

                setResolvedOptions(filtered);
            } finally {
                if (isMountedRef.current && requestId === requestIdRef.current) {
                    setIsLoadingOptions(false);
                }
            }
        }, 400);
    };

    /**
     * Handle selection changes from MultiSelectTagPicker
     */
    const handleSelectionChange = (selectedStrings: string[]): void => {
        props.logger.info(`[${props.type}] onMultiSelectChanged: [${selectedStrings.join(", ")}]`);

        // Convert selected strings back to IPersonaProps objects
        const selectedPersonas = selectedStrings.map((str) => stringToPersonaProps(str, resolvedOptions));

        onAssignedToChanged(selectedPersonas);
        setResolvedOptions([]);
    };

    const assignToMe = async (): Promise<void> => {
        if (props.disabled) return;

        const projectNrn = props.selectedProject?.key;
        const email = props.currentUserEmail;

        if (!projectNrn || !email) {
            props.onError?.(
                props.translate("ACTION_ITEM.ASSIGN_TO_ME_ERROR") as string,
                SEVERE_WARNING_MESSAGE_BAR_TYPE
            );
            return;
        }

        try {
            const teamMembers = await props.projectsService.getTeamMembersNormalized(projectNrn, email);
            let currentAssignees = props.assignees.filter((assignee) => assignee.text !== email);

            if (teamMembers.length === 1) {
                const myPersonaProp = props.formValidationHelpers.getPersonaPropsFromTeamMembers(teamMembers)[0] as any;
                // remove existing record from list
                currentAssignees = currentAssignees.filter(
                    (assignee) => (assignee as any).data?.nrn !== myPersonaProp.data.nrn
                );
                updateAssignees([...currentAssignees, myPersonaProp]);
            } else {
                updateAssignees([...currentAssignees, { text: email }]);
            }
        } catch (error) {
            props.logger.error("AssigneeComponent error", error);
            updateAssignees([...props.assignees, { text: email }]);
        }
    };

    const updateAssignees = (newAssignees: IPersonaProps[]): void => {
        props.onAssigneeChanged(newAssignees);
    };

    const selectedStrings = props.assignees.map((p) => personaPropsToString(p));
    const optionStrings = resolvedOptions.map((p) => personaPropsToString(p));

    const renderAssigneeTag = (x: any) => {
        const optionText = typeof x === "string" ? x : x?.text || "";
        const persona = stringToPersonaProps(optionText, resolvedOptions);

        const email = persona.data?.email;
        const text = persona.text;
        const displayText = text || email || "";

        const initialsSource = displayText.trim();
        const initials = initialsSource
            ? initialsSource.includes("@")
                ? initialsSource
                      .split("@")[0]
                      .slice(0, 2)
                      .toUpperCase()
                : initialsSource
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part) => part[0])
                      .join("")
                      .toUpperCase()
            : "?";

        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                <Avatar color="colorful" name={displayText || undefined} initials={initials} size={20} shape="square" />
                <div>{displayText}</div>
            </div>
        );
    };

    const renderAssigneeOption = (x: any) => {
        const optionText: string = typeof x === "string" ? x : x?.text || "";
        const persona = stringToPersonaProps(optionText, resolvedOptions);

        const name = persona.text?.trim() || "";
        const email = persona.data?.email?.trim() || "";
        const hasNameAndEmail = !!name && !!email && name.toLowerCase() !== email.toLowerCase();
        const displayText: string = name || email || optionText;
        const initialsSource = displayText.trim();
        const initials = initialsSource
            ? initialsSource.includes("@")
                ? initialsSource
                      .split("@")[0]
                      .slice(0, 2)
                      .toUpperCase()
                : initialsSource
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part: string) => part[0])
                      .join("")
                      .toUpperCase()
            : "?";
        if (hasNameAndEmail) {
            return (
                <div className={styles.optionRow}>
                    <Avatar
                        color="colorful"
                        name={displayText || undefined}
                        initials={initials}
                        size={20}
                        shape="square"
                    />
                    <div className={styles.optionTextColumn}>
                        <span className={styles.optionText}>{name}</span>
                        <small className={mergeClasses(styles.optionEmailSubtext, styles.optionSecondaryText)}>
                            {email}
                        </small>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.optionRow}>
                <Avatar color="colorful" name={displayText || undefined} initials={initials} size={20} shape="square" />
                <span className={styles.optionText}>{displayText}</span>
            </div>
        );
    };

    let placeholder = props.placeholder ?? "";
    if (!props.selectedProject) {
        placeholder = "";
    } else if (props.disabled && props.assignees.length === 0) {
        placeholder = props.translate("SHARED.KEYWORD_DROPDOWN.LOADING") as string;
    }

    return (
        <>
            <MultiSelectTagPicker
                label={props.label}
                testid={props.testid ?? "AssigneeFluent9"}
                options={optionStrings}
                selectedOptions={selectedStrings}
                onSelectionChange={handleSelectionChange}
                onInput={handleInput}
                allowCustomInput={true}
                required={props.required}
                disabled={props.disabled}
                placeholder={placeholder}
                itemLimit={props.maxAllowed}
                isLoading={isLoadingOptions}
                loadingText={props.loadingText}
                validationMessage={validationMessage}
                displayFullLineTag={true}
                renderTag={renderAssigneeTag}
                renderOption={renderAssigneeOption}
                showDownArrow={false}
            />

            {props.showAssignToMe &&
                (props.disabled ? (
                    <label className="newforma-assignToMeDisabled">{props.translate("ACTION_ITEM.ASSIGN_TO_ME")}</label>
                ) : (
                    <LinkComponent
                        text={props.translate("ACTION_ITEM.ASSIGN_TO_ME") as string}
                        onClick={assignToMe}
                        className="newforma-assignToMe"
                    />
                ))}
        </>
    );
};

export default withLocalize(AssigneeFluent9Component);
